"use client";

import React, { useRef, useState, useCallback } from 'react';
import { cn } from '@/shared/utils/tw';
import { LEGAL_DOCS, type LegalDocType } from '../const/legal-content';

interface TermsModalProps {
  type: LegalDocType;
  onClose: () => void;
}

/**
 * 이용약관 / 개인정보 처리방침 모달
 *
 * 동의 화면의 링크 클릭 시 외부(구글독스) 이동 대신 인앱 모달로 전문을 표시합니다.
 * 디자인: 태권낭자 리디자인 Figma(3513:16508)의 다크 + 라임 액센트 톤
 */
export const TermsModal: React.FC<TermsModalProps> = ({ type, onClose }) => {
  const doc = LEGAL_DOCS[type];

  // 커스텀 오버레이 스크롤바 — 스크롤 중에만 표시, 1초 후 부드럽게 페이드 아웃
  // (네이티브 ::-webkit-scrollbar는 opacity 트랜지션이 적용되지 않아 직접 렌더)
  const [isScrolling, setIsScrolling] = useState(false);
  const [thumb, setThumb] = useState({ top: 0, height: 0 });
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleScroll = useCallback(() => {
    const el = scrollAreaRef.current;
    if (el && el.scrollHeight > el.clientHeight) {
      const ratio = el.clientHeight / el.scrollHeight;
      setThumb({
        top: (el.scrollTop / el.scrollHeight) * el.clientHeight,
        height: Math.max(ratio * el.clientHeight, 32),
      });
    }
    setIsScrolling(true);
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => setIsScrolling(false), 1000);
  }, []);

  return (
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center p-[20px]"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={doc.title}
    >
      <div
        className="flex flex-col w-full max-w-[420px] h-[80dvh] rounded-[20px] overflow-hidden"
        style={{ backgroundColor: '#1B1E22', border: '1px solid rgba(255,255,255,0.08)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between px-[20px] py-[16px] shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <h2 className="text-white text-[16px] font-bold font-pretendard leading-[1.4] [text-box:trim-both_cap_alphabetic]">
            {doc.title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="flex items-center justify-center w-[28px] h-[28px] rounded-full shrink-0 cursor-pointer"
            style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L11 11M11 1L1 11" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* 본문 (스크롤) — 커스텀 스크롤바: 스크롤 중에만 표시, 스르륵 페이드 */}
        {/* NOTE: 스크롤 영역은 absolute inset-0으로 래퍼에 고정 (flex 내 % 높이 계산 붕괴 방지) */}
        <div className="relative flex-1 min-h-0 overflow-hidden">
          <div
            ref={scrollAreaRef}
            onScroll={handleScroll}
            className={cn(
              "absolute inset-0 overflow-y-auto overscroll-contain px-[20px] py-[16px]",
              "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            )}
          >
          {doc.intro && (
            <p className="text-[13px] font-pretendard leading-[1.6] mb-[16px]" style={{ color: '#C6C9CE' }}>
              {doc.intro}
            </p>
          )}
          {doc.sections.map((section, i) => (
            <section key={i} className={i === 0 ? '' : 'mt-[20px]'}>
              {section.title && (
                <h3 className="text-[14px] font-bold font-pretendard leading-[1.4] mb-[8px] [text-box:trim-both_cap_alphabetic]" style={{ color: '#B4FF45' }}>
                  {section.title}
                </h3>
              )}
              {section.paragraphs?.map((p, j) => (
                <p key={j} className="text-[13px] font-pretendard leading-[1.6] mb-[8px] [text-box:trim-both_cap_alphabetic]" style={{ color: '#C6C9CE' }}>
                  {p}
                </p>
              ))}
              {section.items && (
                <ol className="flex flex-col gap-[6px] pl-[4px]">
                  {section.items.map((item, k) => (
                    <li key={k} className="flex flex-row gap-[8px] text-[13px] font-pretendard leading-[1.6] [text-box:trim-both_cap_alphabetic]" style={{ color: '#C6C9CE' }}>
                      <span className="shrink-0" style={{ color: '#7A7E86' }}>{k + 1}.</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          ))}
          </div>

          {/* 커스텀 스크롤바 썸 */}
          <div
            className="absolute right-[4px] w-[4px] rounded-full pointer-events-none transition-opacity duration-500 ease-out"
            style={{
              top: thumb.top,
              height: thumb.height,
              backgroundColor: 'rgba(255,255,255,0.25)',
              opacity: isScrolling && thumb.height > 0 ? 1 : 0,
            }}
          />
        </div>

        {/* 하단 확인 버튼 */}
        <div className="px-[20px] py-[16px] shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-[12px] rounded-[12px] text-[14px] font-bold font-pretendard cursor-pointer [text-box:trim-both_cap_alphabetic]"
            style={{ backgroundColor: '#B4FF45', color: '#111316' }}
          >
            확인
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
